import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { ReduxState } from "../../../../redux";
import { setSelectedDocumentId } from "../../../../redux/reducers/TransactionManager";
import SelectWithBadge from "./SelectWithBadge";
import { getDocumentName } from "../../Analyze/AnalyzeTransactionsTable/utils";
import { fetchJobDocuments } from "src/redux/reducers/JobDocument";
import { isNullOrUndefined } from "src/utils/utils";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import {
  deleteDocumentFromJob,
  deleteEntityFromTagLevel,
} from "src/docuclipper/api";
import { createAlert } from "src/redux/reducers/Alerts";
import { fetchTagLevels } from "src/redux/reducers/TagLevel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fetchTotals } from "src/redux/reducers/Reconciler";
import { clearJob } from "src/redux/reducers/TemplateField";
import { clearJobData } from "src/redux/reducers/JobData";

type FieldNameType =
  | "bankMode"
  | "invoiceMode"
  | "formMode"
  | "generic"
  | "template";

const getFieldNameFromUrl = (pathname: string): FieldNameType => {
  if (pathname.includes("/transactions")) {
    return "bankMode";
  } else if (pathname.includes("/form")) {
    return "formMode";
  } else if (pathname.includes("/templateDocs")) {
    return "template";
  }
  return "generic"; // default case
};

const DocumentSelector: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { selectedDocumentId, reconciled, badDates } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { data: jobDocs } = useSelector(
    (state: ReduxState) => state.JobDocument
  );
  const { tagLevels } = useSelector((state: ReduxState) => state.TagLevel);

  React.useEffect(() => {
    dispatch(fetchTagLevels());
  }, []);
  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const newDocumentsParam = queryParams.get("newDocuments");
  const l1 = queryParams.get("l1");
  const l2 = queryParams.get("l2");
  const jobId = queryParams.get("jobId");
  const allowedDocumentIds = newDocumentsParam
    ? newDocumentsParam.split(",")
    : null;

  // State to track if filtering is enabled - default to true if query param exists
  const [showOnlyNewDocs, setShowOnlyNewDocs] = useState(!!newDocumentsParam);

  const handleDeleteDocument = async (documentId: string) => {
    confirmAlert({
      title: "Confirm Document Deletion",
      message: "Are you sure you want to delete this document?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              if (l1 && l2) {
                const tagId = tagLevels?.root?.[l1]?.[l2]?.metadata?.tagId;
                if (!tagId) {
                  dispatch(
                    createAlert({
                      message: "Error deleting document",
                      timeout: 0,
                      type: "error",
                    })
                  );
                  return;
                }
                await deleteEntityFromTagLevel(tagId, selectedDocumentId);
              } else if (jobId) {
                await deleteDocumentFromJob(jobId, documentId);
              }
              // Refresh documents list
              dispatch(
                fetchJobDocuments({
                  fieldName: getFieldNameFromUrl(location.pathname),
                })
              );
              dispatch(fetchTotals({}));
              // Clear selection if deleted document was selected
              if (selectedDocumentId === documentId) {
                dispatch(setSelectedDocumentId(null));
              }
            } catch (error) {
              console.error("Error deleting document:", error);
            }
          },
        },
        {
          label: "No",
          onClick: () => {
            // Do nothing if user cancels deletion
          },
        },
      ],
    });
  };

  // useEffect(() => {
  //   if (isNullOrUndefined(jobDocs)) {
  //     dispatch(fetchJobDocuments({}));
  //   }
  // }, [jobDocs, dispatch, l1, l2, jobId]);

  const uniqueDocumentIds = Array.from(
    new Set((jobDocs || []).map((item) => item.documentId))
  ).filter(
    (docId) =>
      !showOnlyNewDocs ||
      (allowedDocumentIds && allowedDocumentIds.includes(docId))
  );

  const documentOptions = uniqueDocumentIds.map((docId) => ({
    value: docId,
    label: getDocumentName(jobDocs || [], docId),
    reconciled: reconciled.documentIds[(docId as any) || ""],
    isBadDate: badDates.documentIds[(docId as any) || ""],
  }));

  const selectedDocument = documentOptions.filter(
    (x) => x.value === selectedDocumentId
  );

  useEffect(() => {
    if (!selectedDocumentId && documentOptions.length > 0) {
      dispatch(setSelectedDocumentId(documentOptions[0].value));
    }
  }, [selectedDocumentId, documentOptions, dispatch]);

  React.useEffect(() => {
    // if (!showOnlyNewDocs) {
    // dispatch(clearJob());
    // dispatch(clearJobData());
    dispatch(fetchTotals({}));
    dispatch(
      fetchJobDocuments({ fieldName: getFieldNameFromUrl(location.pathname) })
    );
    // }
  }, [showOnlyNewDocs, location.pathname]);
  return (
    <div className="flex-column">
      <div className="d-flex align-items-center mb-2">
        <span className="font-weight-bold mr-1">
          Select Document ({uniqueDocumentIds.length} available) ▾
        </span>
        {newDocumentsParam && (
          <div className="ml-3 d-flex align-items-center">
            <input
              type="checkbox"
              id="filterToggle"
              checked={showOnlyNewDocs}
              onChange={(e) => setShowOnlyNewDocs(e.target.checked)}
              className="mr-2"
            />
            <label
              htmlFor="filterToggle"
              className="mb-0"
              style={{ fontSize: "0.9em" }}
            >
              Latest docs only
            </label>
          </div>
        )}
      </div>
      <div className="d-flex align-items-center" style={{ width: "95%" }}>
        <div className="flex-grow-1">
          <SelectWithBadge
            value={selectedDocument.length > 0 ? selectedDocument[0] : null}
            onChange={(e) => {
              dispatch(setSelectedDocumentId(e.value));
            }}
            options={documentOptions}
          />
          {!selectedDocumentId && (
            <small>
              <div className="text-danger">Please select a document</div>
            </small>
          )}
        </div>
        {selectedDocumentId && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="delete-tooltip">Delete document</Tooltip>}
          >
            <Button
              variant="link"
              size="sm"
              className="ml-2"
              onClick={() => handleDeleteDocument(selectedDocumentId)}
            >
              <FontAwesomeIcon icon="trash" />
            </Button>
          </OverlayTrigger>
        )}
      </div>
    </div>
  );
};

export default DocumentSelector;
